/**
 * BmrReplay — /inspector/bmr-replay/$id (v0.12.9 — item #8 / AI-310)
 *
 * Inspector-mode replay of every AI decision on a BMR execution:
 *   - Each BMR_AI_REVIEW_RAN audit row → timeline marker with engines fired
 *   - Each BMR_FINDING_VERDICT row → reviewer verdict card (severity +
 *     engine + verdict + note + confidence)
 *   - Single chronological timeline view + sidebar summary counters.
 *
 * The page is read-only — Inspector mode is deterministic per AI-009.
 */

import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useBmrReplayQuery, type BmrReplayTimelineEvent } from './useBmrReplayQuery';
import styles from './BmrReplay.module.css';

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function verdictBand(verdict: string | null | undefined): 'green' | 'amber' | 'red' | 'grey' {
  switch (verdict) {
    case 'ACCEPTED':           return 'red';
    case 'CONVERTED_TO_DEV':   return 'amber';
    case 'DISMISSED':          return 'grey';
    default:                   return 'amber';
  }
}

function severityBand(sev: string | null | undefined): 'green' | 'amber' | 'red' | 'grey' {
  switch (sev) {
    case 'CRITICAL': return 'red';
    case 'MAJOR':    return 'amber';
    case 'MINOR':    return 'green';
    case 'INFO':     return 'grey';
    default:         return 'grey';
  }
}

export function BmrReplay() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useBmrReplayQuery(id);
  const d = q.data;

  const summary = useMemo(() => {
    return {
      reviews: d?.reviews.length ?? 0,
      verdicts: d?.verdicts.length ?? 0,
      accepted: d?.verdicts.filter((v) => v.verdict === 'ACCEPTED').length ?? 0,
      converted: d?.verdicts.filter((v) => v.verdict === 'CONVERTED_TO_DEV').length ?? 0,
      dismissed: d?.verdicts.filter((v) => v.verdict === 'DISMISSED').length ?? 0,
    };
  }, [d]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/inspector/audit" className={styles.crumb}>Inspector</Link>
          <span aria-hidden>/</span>
          <span className={styles.crumbCurrent}>BMR replay</span>
        </nav>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>BMR audit-chain replay</h1>
            <p className={styles.subtitle}>
              Inspector-mode deterministic view of every AI decision recorded for this batch
              execution. AI never signs or approves — every verdict shown was applied by a
              human reviewer. Source: <code className={styles.code}>audit.system_audit_log</code>.
            </p>
          </div>
          <div className={styles.execChip}>
            <span className={styles.execLabel}>Execution ID</span>
            <span className={styles.execId}>{id}</span>
          </div>
        </div>
      </header>

      <section className={styles.summaryRow} aria-label="Replay summary">
        <div className={styles.sumCard}>
          <span className={styles.sumLabel}>AI reviews run</span>
          <span className={styles.sumValue}>{summary.reviews}</span>
        </div>
        <div className={styles.sumCard}>
          <span className={styles.sumLabel}>Verdicts recorded</span>
          <span className={styles.sumValue}>{summary.verdicts}</span>
        </div>
        <div className={`${styles.sumCard} ${styles.sum_red}`}>
          <span className={styles.sumLabel}>Accepted</span>
          <span className={styles.sumValue}>{summary.accepted}</span>
        </div>
        <div className={`${styles.sumCard} ${styles.sum_amber}`}>
          <span className={styles.sumLabel}>Converted to dev</span>
          <span className={styles.sumValue}>{summary.converted}</span>
        </div>
        <div className={`${styles.sumCard} ${styles.sum_grey}`}>
          <span className={styles.sumLabel}>Dismissed</span>
          <span className={styles.sumValue}>{summary.dismissed}</span>
        </div>
      </section>

      <section className={styles.panel} aria-labelledby="replay-timeline">
        <header className={styles.panelHead}>
          <h2 id="replay-timeline" className={styles.panelTitle}>Chronological timeline</h2>
          <span className={styles.panelCount}>{d?.total_events ?? 0}</span>
        </header>
        {q.isLoading && <p className={styles.empty}>Loading replay…</p>}
        {q.isError && <p className={styles.empty} role="alert">Failed to load replay.</p>}
        {d && d.timeline.length === 0 && (
          <p className={styles.empty}>
            No AI audit events recorded for this execution yet. Run an AI review on the BMR
            to populate the chain.
          </p>
        )}
        {d && d.timeline.length > 0 && (
          <ol className={styles.timeline}>
            {d.timeline.map((event, idx) => (
              <TimelineEntry key={event.audit_id} event={event} index={idx} />
            ))}
          </ol>
        )}
      </section>

      <footer className={styles.advisory}>
        Inspector mode is deterministic — every row above is sourced from
        <code className={styles.code}> audit.system_audit_log</code>, append-only, with
        the original payload preserved. Per Annex 22, AI never produces a verdict; it
        produces a flag, and a human applies the verdict.
      </footer>
    </main>
  );
}

function TimelineEntry({ event, index }: { event: BmrReplayTimelineEvent; index: number }) {
  const isReview = event.kind === 'BMR_AI_REVIEW_RAN';
  const payload = event.payload ?? {};
  if (isReview) {
    return (
      <li className={`${styles.timelineItem} ${styles.timeline_review}`}>
        <span className={styles.timelineDot} aria-hidden />
        <div className={styles.timelineBody}>
          <span className={styles.timelineTime}>{fmtDateTime(event.at)}</span>
          <span className={styles.timelineTitle}>
            AI review #{index + 1} — {((payload as Record<string, unknown>)['engines'] as string[] ?? []).length} engines fired
          </span>
          <div className={styles.engineChips}>
            {((payload as Record<string, unknown>)['engines'] as string[] ?? []).map((eng) => (
              <span key={eng} className={styles.engineChip}>{eng}</span>
            ))}
          </div>
          <span className={styles.timelineSub}>
            {(payload as Record<string, unknown>)['findings_count'] as number} finding(s) emitted ·
            model <code className={styles.code}>{((payload as Record<string, unknown>)['model'] as string) ?? 'Cloud AI'}</code>
          </span>
        </div>
      </li>
    );
  }
  // BMR_FINDING_VERDICT
  const severity = (payload as Record<string, unknown>)['severity'] as string | undefined;
  const verdict = (payload as Record<string, unknown>)['verdict'] as string | undefined;
  const note = (payload as Record<string, unknown>)['note'] as string | undefined;
  const engine = (payload as Record<string, unknown>)['engine'] as string | undefined;
  const confidence = (payload as Record<string, unknown>)['confidence'] as number | undefined;
  return (
    <li className={`${styles.timelineItem} ${styles.timeline_verdict} ${styles[`timeline_${verdictBand(verdict)}`]}`}>
      <span className={styles.timelineDot} aria-hidden />
      <div className={styles.timelineBody}>
        <span className={styles.timelineTime}>{fmtDateTime(event.at)}</span>
        <span className={styles.timelineTitle}>
          Reviewer verdict — {verdict ?? 'unknown'}
        </span>
        <div className={styles.verdictMeta}>
          {severity && <span className={`${styles.verdictChip} ${styles[`band_${severityBand(severity)}`]}`}>{severity}</span>}
          {engine && <span className={styles.engineChip}>{engine}</span>}
          {typeof confidence === 'number' && (
            <span className={styles.confidence}>confidence {Math.round(confidence * 100)}%</span>
          )}
        </div>
        {note && <p className={styles.verdictNote}>{note}</p>}
      </div>
    </li>
  );
}
